import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectCockpitOffering } from 'partner360/features/financialCockpit/model/selectors'

import { CockpitDocumentsBrief } from './components/cockpitDocumentsBrief/CockpitDocumentsBrief'
import { CockpitDocumentsCollapsedSummary } from './components/cockpitDocumentsCollapsedSummary/CockpitDocumentsCollapsedSummary'
import { CockpitPlanExport } from './components/cockpitPlanExport/CockpitPlanExport'
import { DocumentsNavigationView } from './components/documentsNavigationView/DocumentsNavigationView'
import { COCKPIT_DOCUMENTS_BRIEF_VIEW_ENABLED } from './constants'
import { useCockpitDocumentsData } from './hooks/useCockpitDocumentsData'
import type { TDocumentType } from './types'

import styles from './CockpitDocumentsAndLinks.module.scss'

interface ICockpitDocumentsAndLinksProps {
  expanded: boolean
  fullscreen?: boolean
  initialDocumentType?: TDocumentType
  partnerId?: string
  onExpand?: () => void
  onViewDocuments?: (type: TDocumentType) => void
  isManagerCockpitMode?: boolean
}

export const CockpitDocumentsAndLinks = ({
  expanded,
  fullscreen = false,
  partnerId,
  onExpand,
  onViewDocuments,
  isManagerCockpitMode = false,
  initialDocumentType,
}: ICockpitDocumentsAndLinksProps) => {
  const { isPartnerUser } = useUserRoles()
  const [creatingDocuments, setCreatingDocuments] = useState<
    Partial<Record<TDocumentType, boolean>>
  >({})
  const onCreatingChange = useCallback((type: TDocumentType, isCreating: boolean) => {
    setCreatingDocuments((current) => ({ ...current, [type]: isCreating }))
  }, [])
  const offering = useSelector(selectCockpitOffering)
  const readOnly = !partnerId
  const showLinks = !!partnerId

  const { sections } = useCockpitDocumentsData({
    fallbackPartnerId: partnerId,
    isManagerCockpitMode,
  })

  const exportPlan =
    partnerId &&
    offering !== ProgramTypes.Overall &&
    !isManagerCockpitMode &&
    !isPartnerUser &&
    !fullscreen ? (
      <CockpitPlanExport
        key={partnerId}
        partnerId={partnerId}
        expanded={expanded}
        partnerName={sections.find((section) => section.partnerId === partnerId)?.partnerName}
      />
    ) : undefined

  if (!expanded) {
    return (
      <div className={styles.root}>
        {COCKPIT_DOCUMENTS_BRIEF_VIEW_ENABLED ? (
          <CockpitDocumentsBrief
            sections={sections}
            onView={onViewDocuments}
            creatingDocuments={creatingDocuments}
            onCreatingChange={onCreatingChange}
            partnerId={partnerId}
            isManagerCockpitMode={isManagerCockpitMode}
            exportPlan={exportPlan}
          />
        ) : (
          <CockpitDocumentsCollapsedSummary
            exportPlan={exportPlan}
            sections={sections}
            canCreate={!readOnly}
            showLinks={showLinks}
            onExpand={onExpand}
          />
        )}
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <DocumentsNavigationView
        exportPlan={exportPlan}
        sections={sections}
        readOnly={readOnly}
        showLinks={showLinks}
        isManagerCockpitMode={isManagerCockpitMode}
        initialDocumentType={initialDocumentType}
        fullscreen={fullscreen}
      />
    </div>
  )
}

CockpitDocumentsAndLinks.displayName = 'CockpitDocumentsAndLinks'
